import { usePermission } from "../hooks/usePermission";
import React, { useEffect, useState, useMemo } from "react";
import api from "../api/api";
import { useAuth } from "../contexts/AuthContext";
import { Search, Filter, X, Clock, User, FolderKanban, ListChecks, Pencil, Trash2, Ticket } from "lucide-react";
import WorkLogTextDisplay from "../components/WorkLogTextDisplay";

/* ── helpers ─────────────────────────────────────────────── */
const fmtH = (hours: number) => {
  const m = Math.round((hours || 0) * 60);
  if (m === 0) return "0m";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem > 0 ? `${h}h ${rem}m` : `${h}h`;
};
const fmtMin = (m: number) => { const h = Math.floor(m/60); return `${h}:${String(m%60).padStart(2,"0")}`; };

const parseToMinutes = (val: string, unit: string): number => {
  const s = (val || "").trim();
  const hm = s.match(/^(\d+(?:\.\d+)?)h\s*(\d+)m?$/i);
  if (hm) return Math.max(1, Math.round(parseFloat(hm[1]) * 60 + parseInt(hm[2])));
  const h = s.match(/^(\d+(?:\.\d+)?)h$/i);
  if (h) return Math.max(1, Math.round(parseFloat(h[1]) * 60));
  const m = s.match(/^(\d+)m$/i);
  if (m) return Math.max(1, parseInt(m[1]));
  const n = parseFloat(s);
  if (isNaN(n) || n <= 0) return 0;
  return unit === "hr" ? Math.max(1, Math.round(n * 60)) : Math.max(1, Math.round(n));
};

const fmtDate = (val: string | null | undefined) => {
  if (!val) return "-";
  try {
    const d = new Date(val);
    if (isNaN(d.getTime())) return "-";
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return "-"; }
};

/* ── component ───────────────────────────────────────────── */
const Timesheets: React.FC = () => {
  const { user } = useAuth();
  const isEmployee = user?.role === "EMPLOYEE";
  const [entries,  setEntries]  = useState<any[]>([]);
  const [users,    setUsers]    = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [loading,  setLoading]  = useState(false);

  /* filters */
  const [from,        setFrom]        = useState("");
  const [to,          setTo]          = useState("");
  const [employeeId,  setEmployeeId]  = useState("");
  const [projectId,   setProjectId]   = useState("");
  const [typeFilter,  setTypeFilter]  = useState("");
  const [sourceFilter, setSourceFilter] = useState<"all" | "manual" | "auto">("all");
  const [noteEntry, setNoteEntry] = useState<any>(null);
  const [noteText, setNoteText] = useState<string>("");
  const [savingNote, setSavingNote] = useState(false);

  const saveNote = async () => {
    if (!noteEntry) return;
    setSavingNote(true);
    try {
      await api.put(`/timesheets/${noteEntry.id}/note`, { employeeNote: noteText });
      setNoteEntry(null); setNoteText("");
      load();
    } catch (err: any) {
      alert(err?.response?.data?.message || "Failed to save note");
    } finally { setSavingNote(false); }
  };
  const [search,      setSearch]      = useState("");
  /* orphan visibility — entries whose parent (task/ticket/...) was deleted */
  const [orphanMode,  setOrphanMode]  = useState<"all"|"hide"|"only">(() => {
    const v = localStorage.getItem("timesheets.orphanMode");
    return (v === "hide" || v === "only" || v === "all") ? v : "all";
  });
  useEffect(() => { localStorage.setItem("timesheets.orphanMode", orphanMode); }, [orphanMode]);

  /* edit modal */
  const [editEntry,  setEditEntry]  = useState<any | null>(null);
  const [editForm,   setEditForm]   = useState({ minutes: "", unit: "min", date: "", comment: "" });
  const [saving,     setSaving]     = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (from) params.from = from;
      if (to)   params.to   = to;
      if (employeeId) params.employeeId = employeeId;
      if (projectId)  params.projectId  = projectId;
      const r = await api.get("/timesheets", { params });
      setEntries(r.data);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    api.get("/users").then(r    => setUsers(r.data));
    api.get("/projects").then(r => setProjects(r.data));
  }, []);

  const resetFilters = () => {
    setFrom(""); setTo(""); setEmployeeId(""); setProjectId(""); setTypeFilter(""); setSearch("");
    setOrphanMode("all");
    setTimeout(() => api.get("/timesheets").then(r => setEntries(r.data)), 0);
  };

  /* True when the worklog has no parent (parent task/ticket/... was deleted) */
  const isOrphan = (e: any) =>
    !e.taskId && !e.subTaskId && !e.ticketId && !e.projectId;

  /* ── row helpers ─────────────────────────────────────── */
  const getProject = (e: any) =>
    e.project?.name || e.task?.project?.name || e.subTask?.project?.name || null;
  const getTask    = (e: any) => e.task?.title || e.subTask?.title || null;
  const getTicket  = (e: any) => e.ticket ? (e.ticket.ticketNumber || e.ticket.title) : null;
  const getWhmcsTicket = (e: any) => e.whmcsTicket ? `WHMCS #${e.whmcsTicket.tid}` : null;
  const getType    = (e: any) => {
    if (e.taskId)         return "task";
    if (e.subTaskId)      return "subtask";
    if (e.ticketId)       return "ticket";
    if (e.whmcsTicketId)  return "whmcs";
    if (e.projectId)      return "project";
    return "other";
  };

  /* ── permission helper ───────────────────────────────── */
  const canModify = (e: any) => {
    if (!user) return false;
    return true; // Edit permission checked by backend via PermissionGuard
  };

  /* ── filtered list ───────────────────────────────────── */
  const filtered = useMemo(() => {
    let result = entries;
    if (typeFilter) {
      if (typeFilter === "task")    result = result.filter(e => e.taskId || e.subTaskId);
      if (typeFilter === "project") result = result.filter(e => e.projectId && !e.taskId && !e.subTaskId && !e.ticketId);
      if (typeFilter === "ticket")  result = result.filter(e => e.ticketId);
      if (typeFilter === "whmcs")   result = result.filter(e => e.whmcsTicketId);
    }
    if (sourceFilter === "manual") result = result.filter(e => (e.source || "manual") === "manual");
    if (sourceFilter === "auto")   result = result.filter(e => e.source === "whmcs-auto");
    if (orphanMode === "hide") result = result.filter(e => !isOrphan(e));
    if (orphanMode === "only") result = result.filter(isOrphan);
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(e =>
        (e.employee?.name || "").toLowerCase().includes(q) ||
        (getProject(e) || "").toLowerCase().includes(q) ||
        (getTask(e) || "").toLowerCase().includes(q) ||
        (getTicket(e) || "").toLowerCase().includes(q) ||
        (e.comment || "").toLowerCase().includes(q)
      );
    }
    return result;
  }, [entries, typeFilter, search, orphanMode, sourceFilter]);

  // Source stats — computed on all entries (independent of sourceFilter)
  const sourceStats = useMemo(() => {
    let manual = 0, auto = 0;
    for (const e of entries) {
      if (e.source === "whmcs-auto") auto += e.minutes || 0;
      else manual += e.minutes || 0;
    }
    return { manual, auto, total: manual + auto };
  }, [entries]);

  const totalMinutes = filtered.reduce((acc, e) => acc + (e.minutes || 0), 0);
  const hasActiveFilters = from || to || employeeId || projectId || typeFilter || search || orphanMode !== "all";

  const typeLabel: any = { task: "Task", subtask: "Sub-task", ticket: "Ticket", whmcs: "WHMCS", project: "Project", other: "Other" };
  const typeBadge: any = {
    task:    "bg-blue-100 text-blue-700",
    subtask: "bg-indigo-100 text-indigo-700",
    ticket:  "bg-orange-100 text-orange-700",
    whmcs:   "bg-purple-100 text-purple-700",
    project: "bg-green-100 text-green-700",
    other:   "bg-gray-100 text-gray-600",
  };

  /* ── edit actions ────────────────────────────────────── */
  const openEdit = (e: any) => {
    setEditEntry(e);
    setEditForm({
      minutes: String(e.minutes),
      unit: "min",
      date: e.date ? new Date(e.date).toISOString().slice(0, 10) : "",
      comment: e.comment || "",
    });
  };

  const submitEdit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!editEntry) return;
    const mins = parseToMinutes(editForm.minutes, editForm.unit || "min");
    if (!mins || mins < 1) { alert("Minimum 1 minute"); return; }
    setSaving(true);
    try {
      await api.put(`/timesheets/${editEntry.id}`, {
        minutes: mins,
        date: editForm.date,
        comment: editForm.comment,
      });
      setEditEntry(null);
      load();
    } catch (err: any) {
      alert(err?.response?.data?.message || "Failed to update");
    } finally { setSaving(false); }
  };

  const deleteEntry = async (id: string) => {
    if (!window.confirm("Delete this time entry?")) return;
    try {
      await api.delete(`/timesheets/${id}`);
      load();
    } catch (err: any) {
      alert(err?.response?.data?.message || "Failed to delete");
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Timesheets</h2>
        <span className="text-sm text-gray-500">{filtered.length} entries</span>
      </div>

      {/* Source Tabs + Summary */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {(["all","manual","auto"] as const).map(s => {
            const active = sourceFilter === s;
            const label = s === "all" ? "All" : s === "manual" ? "✍️ Manual" : "🟣 Auto (WHMCS)";
            const activeClass = s === "auto"
              ? "bg-purple-600 text-white border-purple-600"
              : s === "manual"
                ? "bg-gray-800 text-white border-gray-800"
                : "bg-blue-600 text-white border-blue-600";
            return (
              <button key={s} onClick={() => setSourceFilter(s)}
                className={`px-4 py-1.5 rounded-lg border text-sm font-medium transition-all ${
                  active ? activeClass : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}>
                {label}
              </button>
            );
          })}
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-center">
            <div className="text-[10px] text-gray-500 uppercase font-semibold">✍️ Manual</div>
            <div className="text-xl font-bold text-gray-800">{fmtMin(sourceStats.manual)}</div>
          </div>
          <div className="rounded-lg border border-purple-200 bg-purple-50 p-3 text-center">
            <div className="text-[10px] text-purple-500 uppercase font-semibold">🟣 Auto (WHMCS)</div>
            <div className="text-xl font-bold text-purple-700">{fmtMin(sourceStats.auto)}</div>
          </div>
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-center">
            <div className="text-[10px] text-blue-500 uppercase font-semibold">🧮 Total</div>
            <div className="text-xl font-bold text-blue-700">{fmtMin(sourceStats.total)}</div>
          </div>
        </div>
      </div>

      {/* Filter Panel */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 space-y-4">
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
          <Filter size={15} /> <span>Filters</span>
          {hasActiveFilters && (
            <button onClick={resetFilters} className="ml-auto flex items-center gap-1 text-xs text-red-500 hover:text-red-700 font-normal">
              <X size={12} /> Reset All
            </button>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">From</label>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">To</label>
            <input type="date" value={to} onChange={e => setTo(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              <span className="flex items-center gap-1"><User size={11} /> Employee</span>
            </label>
            <select value={employeeId} onChange={e => setEmployeeId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white">
              <option value="">All Employees</option>
              {users.map((u: any) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              <span className="flex items-center gap-1"><ListChecks size={11} /> Type</span>
            </label>
            <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white">
              <option value="">All Types</option>
              <option value="task">Tasks</option>
              <option value="project">Projects (Direct)</option>
              <option value="ticket">Tickets</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              <span className="flex items-center gap-1"><ListChecks size={11} /> Orphans</span>
            </label>
            <select value={orphanMode} onChange={e => setOrphanMode(e.target.value as "all"|"hide"|"only")}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white"
              title="Orphan = a worklog whose parent task/ticket was deleted">
              <option value="all">Show all</option>
              <option value="hide">Hide orphans</option>
              <option value="only">Orphans only</option>
            </select>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              <span className="flex items-center gap-1"><FolderKanban size={11} /> Project</span>
            </label>
            <select value={projectId} onChange={e => setProjectId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white">
              <option value="">All Projects</option>
              {projects.map((p: any) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              <span className="flex items-center gap-1"><Search size={11} /> Search</span>
            </label>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search employee, task, project..."
                className="w-full border border-gray-300 rounded-lg pl-8 pr-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
              {search && (
                <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <X size={14} />
                </button>
              )}
            </div>
          </div>
          <div className="flex items-end gap-2">
            <button onClick={load} disabled={loading}
              className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2">
              {loading ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Filter size={14} />}
              Apply Filters
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
          <p className="text-xs text-blue-500 font-medium flex items-center gap-1"><Clock size={12} /> Total Hours</p>
          <p className="text-3xl font-bold text-blue-700 mt-1">{fmtMin(totalMinutes)}</p>
        </div>
        <div className="bg-green-50 rounded-xl p-4 border border-green-200">
          <p className="text-xs text-green-500 font-medium flex items-center gap-1"><User size={12} /> Employees</p>
          <p className="text-3xl font-bold text-green-700 mt-1">
            {new Set(filtered.map(e => e.employee?.id)).size}
          </p>
        </div>
        <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
          <p className="text-xs text-purple-500 font-medium flex items-center gap-1"><FolderKanban size={12} /> Projects</p>
          <p className="text-3xl font-bold text-purple-700 mt-1">
            {new Set(filtered.map(e => getProject(e)).filter(Boolean)).size}
          </p>
        </div>
        <div className="bg-orange-50 rounded-xl p-4 border border-orange-200">
          <p className="text-xs text-orange-500 font-medium flex items-center gap-1"><Ticket size={12} /> Tickets</p>
          <p className="text-3xl font-bold text-orange-700 mt-1">
            {new Set(filtered.map(e => e.ticketId).filter(Boolean)).size}
          </p>
        </div>
        <div className="bg-pink-50 rounded-xl p-4 border border-pink-200">
          <p className="text-xs text-pink-500 font-medium flex items-center gap-1"><Ticket size={12} /> Ticket Entries</p>
          <p className="text-3xl font-bold text-pink-700 mt-1">
            {filtered.filter(e => e.ticketId).length}
          </p>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              {["Employee", "Project", "Task / Ticket", "Type", "Hours", "Description", "Date", "Actions"].map(h => (
                <th key={h} className="px-4 py-3 text-left font-medium text-gray-600 text-xs uppercase tracking-wide">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.map(e => {
              const proj   = getProject(e);
              const task   = getTask(e);
              const ticket = getTicket(e);
              const whmcs  = getWhmcsTicket(e);
              const type   = getType(e);
              return (
                <tr key={e.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                        {(e.employee?.name || "?")[0].toUpperCase()}
                      </div>
                      <span className="font-medium text-gray-900">{e.employee?.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{proj || <span className="text-gray-300">-</span>}</td>
                  <td className="px-4 py-3 text-gray-700 max-w-[180px] truncate">{task || ticket || whmcs || <span className="text-gray-300">-</span>}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${typeBadge[type]}`}>
                        {typeLabel[type]}
                      </span>
                      {e.source === "whmcs-auto" && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold bg-purple-100 text-purple-700 border border-purple-200" title="Auto-computed from WHMCS reply gaps">🤖 Auto</span>
                      )}
                      {isOrphan(e) && (
                        <span className="text-[10px] text-gray-400" title="Parent task/ticket was deleted">⛓️‍💥</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-bold text-blue-600">{fmtMin(e.minutes)}</td>
                  <td className="px-4 py-3 text-gray-700 max-w-[260px] align-top">
                    {(e.comment || e.description) && (
                      <div className="text-xs text-gray-600 mb-1">
                        <WorkLogTextDisplay comment={e.comment} description={e.description} compact />
                      </div>
                    )}
                    {e.source === "whmcs-auto" && (
                      <button
                        onClick={() => { setNoteEntry(e); setNoteText(e.employeeNote || ""); }}
                        className={`text-[11px] flex items-start gap-1 text-left w-full rounded px-1.5 py-1 border ${
                          e.employeeNote
                            ? "bg-amber-50 border-amber-200 text-amber-800 hover:bg-amber-100"
                            : "bg-gray-50 border-gray-200 text-gray-400 hover:bg-gray-100"
                        }`}
                        title="Employee note on auto-computed time">
                        <span>📝</span>
                        <span className="flex-1 leading-snug">
                          {e.employeeNote || <em>Add note (why auto is off)…</em>}
                        </span>
                      </button>
                    )}
                    {!e.comment && !e.description && e.source !== "whmcs-auto" && <span className="text-gray-300">-</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{fmtDate(e.date)}</td>
                  <td className="px-4 py-3">
                    {e.source === "whmcs-auto" ? (
                      <span className="text-[10px] text-purple-500 italic" title="Auto-computed — regenerated on each sync, cannot be edited">🔒 Auto</span>
                    ) : canModify(e) && !isEmployee ? (
                      <div className="flex items-center gap-1">
                        <button onClick={() => openEdit(e)}
                          className="flex items-center gap-1 text-xs bg-gray-50 text-gray-600 border border-gray-200 px-2 py-1 rounded-md hover:bg-gray-100">
                          <Pencil size={11} /> Edit
                        </button>
                        <button onClick={() => deleteEntry(e.id)}
                          className="flex items-center gap-1 text-xs bg-red-50 text-red-500 border border-red-100 px-2 py-1 rounded-md hover:bg-red-100">
                          <Trash2 size={11} /> Delete
                        </button>
                      </div>
                    ) : <span className="text-gray-300 text-xs">—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="text-center py-16 text-gray-400">
            <Clock size={40} className="mx-auto mb-3 opacity-30" />
            <p className="font-medium">No entries found</p>
            {hasActiveFilters && (
              <button onClick={resetFilters} className="mt-2 text-sm text-blue-500 hover:underline">Clear filters</button>
            )}
          </div>
        )}
      </div>

      {/* Employee Note Modal (auto entries only) */}
      {noteEntry && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setNoteEntry(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">📝 Note on Auto Time</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {noteEntry.employee?.name} · WHMCS #{noteEntry.whmcsTicket?.tid || "?"} · {fmtMin(noteEntry.minutes)}
                </p>
              </div>
              <button onClick={() => setNoteEntry(null)} className="text-gray-400 hover:text-gray-600"><X size={20}/></button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-xs text-gray-600">
                لو الوقت المحسوب من WHMCS مش حقيقي، اكتب توضيح هنا. الوقت نفسه مايتغيرش، بس الملاحظة بتتحفظ.
              </p>
              <textarea value={noteText} onChange={e => setNoteText(e.target.value)} rows={4}
                placeholder="مثال: كنت في اجتماع نص الوقت، الحقيقي حوالي ساعة ونص..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 outline-none resize-none"/>
              <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
                <button onClick={() => setNoteEntry(null)}
                  className="border border-gray-300 text-gray-700 px-4 py-1.5 rounded-lg text-sm hover:bg-gray-50">
                  Cancel
                </button>
                <button onClick={saveNote} disabled={savingNote}
                  className="bg-amber-500 text-white px-4 py-1.5 rounded-lg text-sm hover:bg-amber-600 disabled:opacity-50">
                  {savingNote ? "Saving…" : "💾 Save Note"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editEntry && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Edit Time Entry</h3>
                <p className="text-xs text-gray-500 mt-0.5">{editEntry.employee?.name} · {getTask(editEntry) || getProject(editEntry) || "General"}</p>
              </div>
              <button onClick={() => setEditEntry(null)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <form onSubmit={submitEdit} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Time Logged *</label>
                <div className="flex gap-2">
                  <input type="text" required value={editForm.minutes}
                    onChange={e => setEditForm(f => ({ ...f, minutes: e.target.value }))}
                    placeholder={(editForm.unit||"min")==="hr" ? "e.g. 1.5" : "e.g. 30"}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                  <div className="flex border border-gray-300 rounded-lg overflow-hidden text-sm">
                    <button type="button" onClick={() => setEditForm(f=>({...f,unit:"min"}))}
                      className={`px-3 py-2 font-medium transition-colors ${(editForm.unit||"min")==="min"?"bg-blue-600 text-white":"bg-white text-gray-600 hover:bg-gray-50"}`}>min</button>
                    <button type="button" onClick={() => setEditForm(f=>({...f,unit:"hr"}))}
                      className={`px-3 py-2 font-medium transition-colors ${editForm.unit==="hr"?"bg-blue-600 text-white":"bg-white text-gray-600 hover:bg-gray-50"}`}>hr</button>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-1">Also accepts: 1h 30m · 1.5 · 90</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input type="date" value={editForm.date}
                  onChange={e => setEditForm(f => ({ ...f, date: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
                <textarea value={editForm.comment}
                  onChange={e => setEditForm(f => ({ ...f, comment: e.target.value }))}
                  rows={2}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>
              <div className="flex gap-3 pt-1 border-t border-gray-100">
                <button type="button" onClick={() => setEditEntry(null)}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50 text-sm">Cancel</button>
                <button type="submit" disabled={saving}
                  className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50">
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Timesheets;
